import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReceiptPage() {
  const { transactionId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/receipt/${transactionId}`);
        const data = await res.json();
        if (data.success) {
          setReceipt(data.receipt);
        } else {
          setError('Receipt not found');
        }
      } catch {
        setError('Failed to load receipt');
      }
      setLoading(false);
    };
    fetchReceipt();
  }, [transactionId]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
        <span className="text-5xl block mb-4">📄</span>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Receipt Not Found</h2>
        <p className="text-gray-500">{error}</p>
        <a href="/" className="mt-4 inline-block text-orange-500 hover:underline">Go to Home</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="receipt-page">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold">Payment Receipt</h1>
                <p className="text-green-100 text-sm">System Of Success</p>
              </div>
              <span className="text-4xl">✅</span>
            </div>
          </div>

          {/* Receipt Body */}
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <span className="text-sm text-gray-600">Status</span>
              <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium capitalize">{receipt.status}</span>
            </div>

            <div className="space-y-3">
              {[
                { label: 'Transaction ID', value: receipt.transaction_id },
                { label: 'School', value: receipt.school_name },
                { label: 'Plan', value: receipt.plan_name },
                { label: 'Date', value: new Date(receipt.date).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' }) },
              ].filter(r => r.value).map((row, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">{row.label}</span>
                  <span className="text-sm font-medium text-gray-800">{row.value}</span>
                </div>
              ))}
            </div>

            {/* Amount */}
            <div className="bg-gray-50 rounded-xl p-4 text-center mt-4">
              <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
              <p className="text-3xl font-bold text-gray-900" data-testid="receipt-amount">
                {receipt.currency === 'INR' ? '₹' : receipt.currency}{receipt.amount?.toLocaleString('en-IN')}
              </p>
            </div>

            {receipt.razorpay_payment_id && (
              <div className="text-center text-xs text-gray-400">
                Razorpay ID: {receipt.razorpay_payment_id}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 bg-gray-50 space-y-3">
            <p className="text-xs text-gray-400 text-center">{receipt.firm} | {receipt.website}</p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => window.print()} className="px-4 py-2 text-sm text-orange-500 border border-orange-200 rounded-lg hover:bg-orange-50 transition" data-testid="print-receipt-btn">
                Print / Download
              </button>
              <button onClick={() => {
                const url = window.location.href;
                const text = `Payment Receipt - ${receipt.plan_name || 'Shiksha Mitra'}\nAmount: ₹${receipt.amount?.toLocaleString('en-IN')}\nStatus: ${receipt.status}\nTransaction: ${receipt.transaction_id}\n\nView: ${url}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
              }} className="px-4 py-2 text-sm text-white bg-green-500 rounded-lg hover:bg-green-600 transition flex items-center gap-1.5" data-testid="share-whatsapp-btn">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.638-1.467A11.932 11.932 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.171 0-4.178-.672-5.834-1.819l-.418-.281-2.753.871.887-2.679-.305-.442A9.698 9.698 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
                Share on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
